import React, { useState, useEffect } from 'react';
import { fetchReceipts, saveReceipt, fetchCustomers, deleteReceipt } from '../../services/api';
import './BillReceipt.css';

export default function BillReceipt({ user }) {
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const companyId = user?.Companyid || user?.company_id || "1";
  
  // Date range for fetching - defaulting to current month
  const now = new Date();
  
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
      // YYYY-MM-DD to DD-MM-YYYY
      const [y, m, d] = dateStr.split("-");
      return `${d}-${m}-${y}`;
    }
    return dateStr;
  };

  const formatDateForUI = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("-") && dateStr.split("-")[0].length <= 2) {
      // DD-MM-YYYY to YYYY-MM-DD
      const [d, m, y] = dateStr.split("-");
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  };

  const getTodayForUI = () => {
    const d = new Date();
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  };

  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });

  const [filterName, setFilterName] = useState("");

  const [form, setForm] = useState({
    date: '',
    customer_id: '',
    received_amount: '',
    mode: ''
  });

  useEffect(() => {
    loadCustomers();
  }, [companyId]);

  useEffect(() => {
    loadReceipts();
  }, [companyId, filterName, dateRange.from, dateRange.to]);

  const loadCustomers = async () => {
    try {
      const res = await fetchCustomers(companyId);
      if (res.success) {
        const raw = res.Response || res.data || [];
        setCustomers(Array.isArray(raw) ? raw : [raw]);
      }
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const loadReceipts = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        Companyid: companyId,
        Name: filterName, 
        FromDate: formatDateForAPI(dateRange.from),
        ToDate: formatDateForAPI(dateRange.to)
      };
      
      const res = await fetchReceipts(payload);
      if (res.success) {
        const raw = res.Response || [];
        let list = Array.isArray(raw) ? raw : [raw];
        
        // Client-side filter fallback if backend returns more data than requested
        if (filterName) {
          list = list.filter(r => 
            (r.Customer || r.Name || '').toLowerCase().includes(filterName.toLowerCase())
          );
        }
        
        // Map API fields to UI fields
        const mapped = list.map((r, i) => ({
          id: i,
          date: r.Date1,
          party: r.Customer,
          received: parseFloat(r.Amount) || 0,
          mode: r.PaymentMode,
          Recid: r.Recid,
          IDS: r.IDS,
          Receiptid: r.Receiptid,
          // API doesn't seem to provide receiptNo, billNo, billAmt in the example
          receiptNo: `REC-${i+100}`, 
          billNo: '-',
          billAmt: 0
        }));
        
        setRecords(mapped);
      }
    } catch (err) {
      console.error('Failed to load receipts:', err);
      setError('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.date || !form.customer_id || !form.received_amount || !form.mode) {
      alert('Please select a Date, Party, Amount and Payment Mode');
      return;
    }

    const customer = customers.find(c => (c.Customerid || c.id) == form.customer_id);
    if (!customer) return;

    const payload = {
      Companyid: companyId,
      Name: customer.Name || customer.name,
      Amount: String(form.received_amount),
      Date1: formatDateForAPI(form.date),
      PaymentMode: form.mode.toUpperCase(),
      IDS: editId ? String(editId) : "0",
      Receiptid: editId ? String(editId) : "0"
    };

    setLoading(true);
    try {
      const res = await saveReceipt(payload);
      if (res.success || res.Response === "UPDATED") {
        alert(res.message || 'Receipt saved successfully');
        setShowForm(false);
        setForm({
          date: '',
          customer_id: '',
          received_amount: '',
          mode: ''
        });
        loadReceipts();
      } else {
        alert(res.message || 'Failed to save receipt');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Error saving receipt');
    } finally {
      setLoading(false);
    }
  };



  const openEdit = (r) => {
    const customer = customers.find(c => (c.Name || c.name) === r.party);
    const receiptId = r.Receiptid || r.Recid || r.IDS || r.id || "0";
    setEditId(receiptId);
    setForm({
      date: formatDateForUI(r.date || r.Date1 || getTodayForUI()),
      customer_id: customer ? (customer.Customerid || customer.id) : '',
      received_amount: r.received || r.Amount || '',
      mode: r.mode || r.PaymentMode || 'Cash'
    });
    setShowForm(true);
  };
 
  const openAdd = () => {
    setEditId(null);
    setForm({
      date: '',
      customer_id: '',
      received_amount: '',
      mode: ''
    });
    setShowForm(true);
  };
 
  const totalReceived = records.reduce((s, r) => s + (r.received || 0), 0);

  return (
    <div className="bill-receipt-page">
      <div className="page-header">
        <h2 className="page-title">Bill Receipt</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          
          <select 
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={filterName}
            onChange={e => setFilterName(e.target.value)}
          >
            <option value="">All Parties</option>
            {customers.map(c => (
              <option key={c.Customerid || c.id} value={c.Name || c.name}>
                {c.Name || c.name}
              </option>
            ))}
          </select>

          <div className="date-filter" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              style={{ width: 130, border: 'none', padding: 0 }}
              value={dateRange.from}
              onChange={e => setDateRange({...dateRange, from: e.target.value})}
            />
            <span>to</span>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              style={{ width: 130, border: 'none', padding: 0 }}
              value={dateRange.to}
              onChange={e => setDateRange({...dateRange, to: e.target.value})}
            />
            <button onClick={loadReceipts} className="btn btn-primary btn-sm" style={{ padding: '2px 8px' }}>
              <i className="bi bi-arrow-repeat"></i>
            </button>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={openAdd}
          >
            ＋ New Receipt
          </button>
        </div>
      </div>

      <div
        style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '12px 16px',
          marginBottom: 14,
          borderLeft: '4px solid var(--secondary)',
          display: 'inline-block'
        }}
      >
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--secondary)' }}>
          ₹{totalReceived.toLocaleString()}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
          Total Received
        </div>
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? 'Edit Bill Receipt' : 'New Bill Receipt'}</div>
          <div className="form-row">
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Party</label>
              <select
                className="form-control"
                value={form.customer_id}
                onChange={e => setForm({ ...form, customer_id: e.target.value })}
              >
                <option value="">Select Party</option>
                {customers.map(c => (
                  <option key={c.Customerid || c.id} value={c.Customerid || c.id}>
                    {c.Name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Amount (₹)</label>
              <input
                type="number"
                className="form-control"
                value={form.received_amount}
                onChange={e => setForm({ ...form, received_amount: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Mode</label>
              <select
                className="form-control"
                value={form.mode}
                onChange={e => setForm({ ...form, mode: e.target.value })}
              >
                <option value="">Select Mode</option>
                <option>Cash</option>
                <option>NEFT</option>
                <option>UPI</option>
                <option>Cheque</option>
              </select>
            </div>
          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              <i className="bi bi-save me-1"></i> 
              {loading 
                ? (editId ? 'Updating...' : 'Saving...') 
                : (editId ? 'Update' : 'Save')}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">
          Bill Receipts — {records.length} records
        </div>
        <div className="table-wrapper">
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading receipts...
            </div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>
              {error}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="text-center">Receipt No.</th>
                  <th className="text-center">Date</th>
                  <th className="text-left">Party</th>
                  <th className="text-right">Received (₹)</th>
                  <th className="text-center">Mode</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No records found for this period</td>
                  </tr>
                ) : (
                  records.map((r, i) => (
                      <tr key={i}>
                        <td className="text-center" style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 700 }}>
                          {r.receiptNo || '—'}
                        </td>
                        <td className="text-center">{r.date}</td>
                        <td className="text-left" style={{ fontWeight: 600 }}>{r.party || '—'}</td>
                        <td className="text-right" style={{ fontWeight: 700, color: 'var(--secondary)' }}>
                          ₹{(+r.received || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="text-center">
                          <span className="badge badge-info">{r.mode}</span>
                        </td>
                        <td className="text-center">
                          <button className="icon-btn" onClick={() => openEdit(r)} title="Edit Receipt">
                            <i className="bi bi-pencil-square"></i>
                          </button>
                        </td>
                      </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}